import { useEditorStore } from '~/shell/Store';

type SaveResult = { error?: boolean } | undefined;

/**
 * One optional field's saves in one editor session. The Text that shows the
 * field is replaced whenever the editor redraws the slide; the field's saves
 * live here instead, so they still go one at a time in the order the author
 * made them, and what the server last confirmed outlives the Text that saw it.
 */
export class OptionalFieldWrites {
  /** Whether the server last confirmed text for the field. */
  private confirmedHasText: boolean;
  /** Whether the last save was refused: the editor's copy is then unconfirmed. */
  private refused = false;
  /** Saves waiting or on their way, in order (true = saves text). */
  private pending: boolean[] = [];
  /** Where a field's saves are compared by value (a post's author name). */
  private confirmedValue?: string;
  private pendingValues: Array<string | undefined> = [];
  private queue: Promise<void> = Promise.resolve();
  private listeners = new Set<(hasText: boolean) => void>();

  constructor(
    private readonly session: object,
    hasText: boolean,
    value?: string
  ) {
    this.confirmedHasText = hasText;
    this.confirmedValue = value;
  }

  /**
   * The editor's copy of the field, as a Text shows it. Once every save has
   * settled and none was refused, that copy is what the server holds.
   */
  seen(hasText: boolean, value?: string) {
    if (!this.pending.length && !this.refused) {
      this.confirmedHasText = hasText;
      this.confirmedValue = value;
    }
  }

  /**
   * Whether emptying the field must save the clear: the server may still hold
   * text (it last confirmed text, or a save of text is still to land), unless
   * a clear is already the last save still to land.
   */
  needsClear() {
    const { pending } = this;
    if (pending.length && !pending[pending.length - 1]) return false;
    return this.confirmedHasText || pending.includes(true);
  }

  /** Whether the server holds text for the field, as far as it has confirmed. */
  hasText() {
    return this.confirmedHasText;
  }

  /** The value the field's saves leave it with: the last still to land, or the confirmed one. */
  latestValue() {
    return this.pendingValues.length
      ? this.pendingValues[this.pendingValues.length - 1]
      : this.confirmedValue;
  }

  /**
   * Saves the field once every earlier save of it has settled. A save still
   * waiting when the editor has moved to another session (course) is dropped:
   * it must not reach that session.
   */
  save(writesText: boolean, write: () => unknown, value?: string) {
    const run = async () => {
      let result: SaveResult = { error: true };
      if (currentSession() === this.session) {
        try {
          result = (await write()) as SaveResult;
        } catch {
          result = { error: true };
        }
      }
      this.pending.shift();
      const saved = this.pendingValues.shift();
      this.refused = Boolean(result?.error);
      if (!this.refused) {
        this.confirmedHasText = writesText;
        this.confirmedValue = saved;
      }
      if (!this.pending.length) {
        this.listeners.forEach(listener => listener(this.confirmedHasText));
      }
    };
    // Starts at once when no save is still to land, otherwise after the last one.
    const idle = this.pending.length === 0;
    this.pending.push(writesText);
    this.pendingValues.push(value);
    this.queue = idle ? run() : this.queue.then(run);
  }

  /** Hears whenever the field's saves have all settled. */
  onSettled(listener: (hasText: boolean) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

/** Saves made without an editor connection (stories, tests) share one session. */
const NO_SESSION = {};
const currentSession = (): object =>
  useEditorStore.getState().apiService ?? NO_SESSION;

const sessions = new WeakMap<object, Map<string, OptionalFieldWrites>>();

/** An optional field's saves in the editor's current session. */
export const optionalFieldWrites = (
  subitemId: string,
  field: string,
  hasText: boolean,
  value?: string
) => {
  const session = currentSession();
  let fields = sessions.get(session);
  if (!fields) {
    fields = new Map();
    sessions.set(session, fields);
  }
  const key = `${subitemId}.${field}`;
  let writes = fields.get(key);
  if (!writes) {
    writes = new OptionalFieldWrites(session, hasText, value);
    fields.set(key, writes);
  }
  return writes;
};
